import { useEffect, useMemo, useState } from 'react'

import { useZustand } from '../../hooks/useZustand'
import {
    useContextsStore,
    selectIsOwnView,
} from '../../stores/useContextsStore'
import {
    useOnboardingStore,
    GUIDE_SIGNALS,
} from '../../stores/useOnboardingStore'
import { STAGES } from './copy'
import useIntroHold from './useIntroHold'

// Long enough to land on, short enough for a full pass while the step is read.
const CYCLE_INTERVAL_MS = 1500
// Keeps the first halo off a graph whose layout is still settling; re-arms on a return to Topics.
const SETTLE_DELAY_MS = 2000
// Ranked by influence, so the head of the list is the biggest topics.
const MAX_SPOTLIGHT_TOPICS = 4

const NO_SPOTLIGHT = []

/**
 * Cycles the spotlight across the biggest topics for the whole topics step.
 * Returns the community ids to spotlight now — one, or none; GraphTopicSpotlight
 * draws the ring. Stops the moment a topic is picked, off the Topics layer, and
 * on someone else's graph. The interval is presentation, not coordination.
 */
export default function useTopicSpotlight(topics) {
    const isTopicsStep = useOnboardingStore(
        state => state.status === 'active' && state.stage === STAGES.TOPICS
    )
    const topicPicked = useOnboardingStore(
        state => !!state.signals[GUIDE_SIGNALS.TOPIC_SELECTED]
    )
    const isOwnView = useContextsStore(selectIsOwnView)
    const graphLayer = useZustand(state => state.currentGraphMode)
    const introHeld = useIntroHold()

    const candidateIds = useMemo(
        () =>
            (topics || [])
                .slice(0, MAX_SPOTLIGHT_TOPICS)
                .map(topic => topic.id),
        [topics]
    )
    const candidateCount = candidateIds.length

    const running =
        isTopicsStep &&
        !topicPicked &&
        !introHeld &&
        isOwnView &&
        graphLayer === 'topics' &&
        candidateCount > 0

    const [spotlightIndex, setSpotlightIndex] = useState(null)

    useEffect(() => {
        if (!running) {
            setSpotlightIndex(null)
            return
        }
        let cycle = null
        const settle = window.setTimeout(() => {
            setSpotlightIndex(0)
            cycle = window.setInterval(
                () => setSpotlightIndex(index => (index + 1) % candidateCount),
                CYCLE_INTERVAL_MS
            )
        }, SETTLE_DELAY_MS)
        return () => {
            window.clearTimeout(settle)
            if (cycle) window.clearInterval(cycle)
        }
    }, [running, candidateCount])

    return useMemo(() => {
        if (spotlightIndex === null || candidateIds.length === 0) {
            return NO_SPOTLIGHT
        }
        return [candidateIds[spotlightIndex % candidateIds.length]]
    }, [spotlightIndex, candidateIds])
}
